import Stripe from "stripe";

import {
  AdminDashboardState,
  CaseSearchState,
  OrderMedicationsState,
  OrderSearchState,
  RedoCancelState,
  RedoInvoiceState,
  RedoReplaceState,
  RedoScreenState,
  ShipAddressState,
  UserAddressSaveState,
  ShipScreenState,
  UserSearchStatus,
} from "./admin";
import { PaymentMethodForm } from "./billing";
import { Case, Order } from "./case";
import { ConditionTypes } from "./condition";
import { PaymentAttemptOutcome } from "./paymentRetry";
import { Questions } from "./questionnaire";
import { RemovedMedicationsState } from "./removedMedication";
import { Address, User } from "./user";

export enum ErrorEvents {
  ACCESS_DENIED = "Access Denied",
  SEND_CASE = "sendCase",
  PAY_CASE = "payCase",
  GET_CASE = "getCase",
  LOGIN = "login",
  NOT_FOUND = "Not Found",
  BAD_REQUEST = "Bad Request",
  SERVER_ERROR = "Server Error",
  PENDING_INVOICE = "Case is not paid.",
  FAILED_INVOICE = "Invoice failed.",
  ERR_NETWORK = "Network Error",
  UPSELL_FAILURE = "Upsell Failure",
  ADD_PAYMENT_METHOD = "addPaymentMethod",
}

export enum ServicesTypes {
  VISIT_FEE = "Visit Fee",
  SHIPPING_FEE = "Shipping Fee",
}

export interface ProductMetadata {
  name: string;
  // medication_1, medication_2:"priceid_2323" ...
  [key: string]: string;
}

export interface Product {
  id: string;
  name: ConditionTypes | ServicesTypes;
  metadata?: ProductMetadata;
  units: number;
}
export interface RunwayPrice {
  id: string;
  product: Product;
  unit_amount: number;
}

/**
 * A RootEntity is an interface that represents
 * the redux state pattern, which behavior repeats among
 * the different reducers, considering that a state may always:
 * - Have a mutation in progress (isPending)
 * - Contain a data presentation layer.
 * - Hold mutation error details.
 */
export interface RootEntity<T> {
  isPending: boolean;
  success: { type: "write" | "read" | null };
  data: T;
  error: {
    message?: string;
    event?: string;
    paymentOutcome?: PaymentAttemptOutcome;
  };
}

export interface CaseState {
  newCase: RootEntity<Case>;
  cases: RootEntity<RootEntity<Case>[]>;
}

export interface PricesState {
  case: RootEntity<RunwayPrice>;
  shipping: RootEntity<Stripe.ShippingRate[]>;
  prescriptions: RootEntity<RunwayPrice[]>;
}

export interface RouterData {
  location: {
    pathname: string;
    search: string;
    hash: string;
    key: string;
    query: {
      [key: string]: string;
    };
  };
}
export type PartnerType = "other" | "insurance";

export interface Partner {
  clientKey?: string;
  clientSecret?: string;
  name: string;
  type: PartnerType;
  spreadsheetId?: string;
  promocodes: string[];
  transactionsCount?: number;
}

export type PartnerUpdate = Partial<
  Omit<Partner, "clientKey" | "clientSecret" | "transactionsCount">
>;

export interface Transaction {
  id: string;
  userEmail: string;
  invoiceId?: string;
  PartnerClientKey: string;
}

export interface UserSearchData {
  id: string;
  customer_id: string;
  firstName: string;
  addresses: Address[];
  stateOfResidence: string;
  lastName: string;
  email: string;
  stripeId: string;
  mdiId: string;
  address: string;
  phoneNumer: string;
  dob: string;
}

export interface Credentials {
  clientKey: string;
  clientSecret: string;
}

export interface PromoCode {
  promoCodeId: string;
  times_redeemed: number;
  first_time_redeemed: Date;
  last_time_redeemed: Date;
  code: string;
}

export interface Redemption {
  createdAt: Date;
  User: {
    email: string;
  };
}

export interface AdminState {
  partners: Partner[];
  users: UserSearchData;
  credentials: Credentials;
  transactions: Transaction[];
  cases: Case[];
  userCases: Case[];
  redoScreen: RedoScreenState;
  redoInvoice: RedoInvoiceState;
  redoCancel: RedoCancelState;
  redoReplace: RedoReplaceState;
  orderMedications: Record<number, OrderMedicationsState>;
  orders: Order[];
  promoCodes: PromoCode[];
  redemptions: Redemption[];
  cancelationError: boolean;
  caseShippingError: boolean;
  orderShippingError: boolean;
  orderSyncError: boolean;
  cancellingOrderId: string | null;
  deletingPartnerId: string | null;
  isUpdatingAddress: boolean;
  isCaseShippingUpdating: boolean;
  orderActionsInFlight: Record<string, boolean>;
  isSearchingUsers: boolean;
  isSearchingCases: boolean;
  isSearchingOrders: boolean;
  isSearchingPromoCodes: boolean;
  userSearchStatus: UserSearchStatus;
  caseSearch: CaseSearchState;
  orderSearch: OrderSearchState;
  shipScreen: ShipScreenState;
  shipAddress: ShipAddressState;
  userAddressSave: UserAddressSaveState;
  lastCaseSearchParams: Record<string, unknown> | null;
}

export interface RootState {
  router: RouterData;
  user: RootEntity<User> & { _persist?: { version: number; rehydrated: boolean } };
  case: CaseState;
  removedMedications: RemovedMedicationsState;
  price: PricesState;
  questions: Questions;
  prescriptionPrices: RunwayPrice[];
  paymentMethodForm: PaymentMethodForm;
  messages: {};
  ui: {
    error: string;
    loading: true;
    home_address_status: boolean;
    notification: {
      message: boolean;
    };
  };
  admin: AdminState;
  adminDashboard: AdminDashboardState;
}

export interface DateFieldValue {
  1: string;
  2: string;
  3: string;
  4: string;
  5: string;
  6: string;
  7: string;
  8: string;
}

export const safeParse = <T>(str: string): T | null => {
  try {
    const parsed = JSON.parse(str);
    return typeof parsed === "object" && parsed !== null ? parsed : null;
  } catch (e) {
    return null;
  }
};
